import React, { Component } from 'react'
import { icons } from 'assets'
import styles from './ProjectInfoForm.module.scss'
import {
  Button, LabeledDropdown, LabeledInput, LabeledTextarea,
  Tag, LabeledGalleryDropZone, RelatedMediaFormSection,
  IconButton, PersonsFormSection
} from 'components'
import { PROJECT_STORE, GENERAL_STORE } from 'store'
import { inject, observer } from 'mobx-react'
import AttachedProductsSection from './AttachedProductsSection'
import VideoSection from './VideoSection'
import TypeSection from './TypeSection'
import { Props, State, genreOptions } from './types'
import { getYears } from './utils'
import { ProjectTypes, ApiVovSubtypes, GenreTypes, ProjectDTO } from 'models'

class ProjectInfoForm extends Component<Props, State> {
  isSubmitWithEnterKeyAllowed: boolean;
  gotParentInfo: boolean;
  imagesRef: React.RefObject<{
    handleImages: () => Promise<any>;
    getImages: () => any[];
  }>;

  videoRef: React.RefObject<{
    handleVideo: () => Promise<any>;
    getVideoUrl: () => string;
  }>;

  constructor (props: Props) {
    super(props)
    this.state = {
      isScrollOnTop: true,
      name: '',
      type: '' as ProjectTypes,
      subtype: '' as ApiVovSubtypes,
      genres: [],
      year: null,
      tags: [],
      info: '',
      images: [],
      actors: [],
      directors: [],
      producers: [],
      relatedMedia: [],
      brands: [],
      videoUrl: null
    }

    this.imagesRef = React.createRef()
    this.videoRef = React.createRef()
    this.isSubmitWithEnterKeyAllowed = true
    this.gotParentInfo = !props.parentId

    this.handleTagInput = this.handleTagInput.bind(this)
    this.handleOnScroll = this.handleOnScroll.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
  }

  componentDidMount () {
    const {
      [PROJECT_STORE]: {
        getProjectById
        // getProjectTypes
      },
      project,
      parentId
    } = this.props

    if (parentId) getProjectById(parentId, true, true)
    // getProjectTypes();
    this.feedProjectDetails(project)
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps (props) {
    this.feedProjectDetails(props.project)
  }

  componentDidUpdate () {
    if (this.gotParentInfo) return
    const parentProject = this.props[PROJECT_STORE].parentProjectDetails

    if (JSON.stringify(parentProject) === '{}') return

    this.feedProjectDetails({
      ...parentProject,
      name: '',
      type: '',
      subtype: '',
      videoUrl: null
    })
    this.gotParentInfo = true
  }

  feedProjectDetails (project) {
    if (!project) return

    this.setState({
      name: project.name || '',
      type: (project.type || '') as ProjectTypes,
      subtype: project.subtype || '',
      genres: project.genres || [],
      year: project.year || null,
      tags: project.tags ? project.tags.split(',') : [],
      info: project.info || '',
      images: project.images || [],
      actors: project.actors || [],
      directors: project.directors || [],
      producers: project.producers || [],
      relatedMedia: project.relatedMedia || [],
      brands: project.brands || [],
      videoUrl: project.videoUrl || null
    })
  }

  handleTagInput (tag: string) {
    tag = tag.trim().toLocaleLowerCase()
    const { tags } = this.state

    if (tags.includes(tag)) return

    this.setState({ tags: [...tags, tag] })
  }

  handleOnScroll (e: React.UIEvent<HTMLFormElement>) {
    const { isScrollOnTop: wasScrollOnTop } = this.state
    const isScrollOnTop = e.currentTarget.scrollTop === 0
    if (wasScrollOnTop !== isScrollOnTop) this.setState({ isScrollOnTop })
  }

  handleSubmit () {
    if (!this.isSubmitWithEnterKeyAllowed) return

    const {
      [PROJECT_STORE]: {
        createProject, editProject
      },
      [GENERAL_STORE]: { showError, setLoadingOn },
      project,
      parentId
    } = this.props

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { isScrollOnTop, ...stateProjectDetails } = this.state

    if (!stateProjectDetails.name) {
      showError('Please provide the NAME of the project.')
      return
    }

    if (!stateProjectDetails.type) {
      showError('Please set TYPE of the project.')
      return
    }

    setLoadingOn()

    const projectDetails = {
      ...stateProjectDetails,
      tags: stateProjectDetails.tags.join()
    }

    this.imagesRef.current.handleImages()
      .then(() => {
        projectDetails.images = this.imagesRef.current.getImages()
        return this.videoRef.current.handleVideo()
      })
      .then(() => {
        projectDetails.videoUrl = this.videoRef.current.getVideoUrl()

        project
          ? editProject({ id: project.id, ...projectDetails } as ProjectDTO)
          : createProject({ parentId, ...projectDetails } as unknown as ProjectDTO)
      })
      .catch(err => showError(err.message))
  }

  render () {
    const {
      isScrollOnTop,
      name,
      type,
      subtype,
      genres,
      year,
      tags,
      info,
      images,
      actors, directors, producers,
      relatedMedia,
      brands,
      videoUrl
    } = this.state
    const {
      [PROJECT_STORE]: {
        projectToView,
        parentProjectDetails,
        triggerNewVideoProcessingForPtojectToView
      },
      [GENERAL_STORE]: { push },
      parentId,
      project
    } = this.props
    const editProject = !!project

    return (
      <div className={styles.container}>

        <h1 className={!isScrollOnTop ? styles.overEffect : ''}>
          {
            editProject
              ? `Edit "${project.name}" project`
              : parentId
                ? `Create a subproject to "${parentProjectDetails.name}"`
                : 'Create new project'
          }
        </h1>

        <form onScroll={this.handleOnScroll}>
          <VideoSection
            ref={this.videoRef}
            videoUrl={videoUrl}
            readyForEdit={projectToView.readyForEdit}
            triggerNewVideoProcessing={triggerNewVideoProcessingForPtojectToView}
            openInEditor={() => push(`/editor/${projectToView.id}`)}
          />

          <div>
            <LabeledInput
              label='Title'
              placeholder='Type the title or the name of the project'
              onChange={value => this.setState({ name: value })}
              value={name}
            />

            <TypeSection
              type={type}
              subtype={subtype}
              setTypeAndSubtype={(typeValue, subtypeValue) => {
                this.setState({ type: typeValue as ProjectTypes, subtype: subtypeValue })
              }}
              editProject={editProject}
            />

            <div className={styles.dropdownContainer}>
              <LabeledDropdown
                maxSelections={3}
                placeholder='Choose up to 3 genres'
                options={genreOptions}
                label='Genres'
                className={styles.genresDropdown}
                onSelect={(selectedGenres: GenreTypes[]) =>
                  this.setState({ genres: selectedGenres })
                }
                selectedOptions={genres}
              />
              <LabeledDropdown
                options={getYears()}
                label='Year'
                className={styles.yearDropdown}
                onSelect={arr => this.setState({ year: Number(arr[0]) })}
                selectedOptions={[year ? year.toString() : '']}
              />
            </div>

            <LabeledInput
              label='Tags'
              placeholder='Type a tag for a project, press enter to set a tag'
              keysToHandle={['Enter']}
              onKeyDown={this.handleTagInput}
              onFocus={() => { this.isSubmitWithEnterKeyAllowed = false }}
              onBlur={() => { this.isSubmitWithEnterKeyAllowed = true }}
            />

            <div className={styles.tagsContainer}>
              {tags.map((tag, index) =>
                <Tag key={index} tag={tag}>
                  <IconButton
                    icon={icons.closeX}
                    className={styles.tagIcon}
                    onClick={() => this.setState({ tags: tags.filter(t => t !== tag) })}
                  />
                </Tag>
              )}
            </div>
          </div>

          <div>
            <LabeledTextarea
              onChange={value => this.setState({ info: value })}
              value={info}
            />
          </div>

          <LabeledGalleryDropZone
            ref={this.imagesRef}
            initialImages={images}
          />

          <PersonsFormSection
            label='Actors'
            personsList={actors}
            setPersonsList={actorsList => this.setState({ actors: actorsList })}
          />

          <PersonsFormSection
            label='Directors'
            personsList={directors}
            setPersonsList={directorsList => this.setState({ directors: directorsList })}
          />

          <PersonsFormSection
            label='Producers'
            personsList={producers}
            setPersonsList={producersList => this.setState({ producers: producersList })}
          />

          <RelatedMediaFormSection
            selectedProjectsList={relatedMedia}
            setSelectedProjectsList={projects =>
              this.setState({ relatedMedia: projects })
            }
          />

          <AttachedProductsSection
            brands={brands}
            setBrands={brandsList => this.setState({ brands: brandsList })}
          />

          <div className={styles.buttonsContainer}>
            <Button
              type='cancel'
              onClick={() => push('/projects')}
            // className={styles.addButton}
            >
              Discard Changes
            </Button>
            <Button
              type='submit'
              onClick={this.handleSubmit}
              className={styles.saveBttn}
            >
              Save Changes
            </Button>
          </div>
        </form>

      </div>
    )
  }
}

export default inject(PROJECT_STORE, GENERAL_STORE)(observer(ProjectInfoForm))
